import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { hpaGetAccess } from "../../lib/hpaApi";

/**
 * Block 11.1 · § 45, § 48, § 50
 *
 * Guard für die authentifizierte HPA-Journey. Fail-closed:
 *   - nicht eingeloggt → /auth/login (bestehende Redirect-Kette)
 *   - Netzwerkfehler / 5xx → Kein Rendern der HPA (fail-closed, keine Ausnahme)
 *   - has_preview_access=false → Redirect auf die öffentliche Methoden-Seite
 *
 * Backend bleibt Enforcement (§ 45). Dieser Guard verbessert nur UX.
 */
export function RequireHpaPreviewAccess({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [state, setState] = useState({ loading: true, access: null, error: null });

  useEffect(() => {
    let alive = true;
    if (authLoading) return;
    if (!user) {
      setState({ loading: false, access: null, error: null });
      return;
    }
    hpaGetAccess()
      .then((data) => {
        if (!alive) return;
        setState({ loading: false, access: data, error: null });
      })
      .catch((err) => {
        if (!alive) return;
        setState({ loading: false, access: null, error: err });
      });
    return () => {
      alive = false;
    };
  }, [user, authLoading]);

  if (authLoading || state.loading) {
    return (
      <div
        className="hpa hpa-canvas min-h-screen"
        data-testid="hpa-guard-loading"
      />
    );
  }

  if (!user) {
    const target = `/auth/login?redirect=${encodeURIComponent(
      location.pathname + location.search
    )}`;
    return <Navigate to={target} replace />;
  }

  // fail-closed: any error, or explicit denial → redirect to public methode page
  if (
    state.error ||
    !state.access ||
    state.access.enabled !== true ||
    state.access.has_preview_access !== true
  ) {
    return (
      <Navigate to="/methoden/human-potential-analysis" replace state={{ hpaGate: "denied" }} />
    );
  }

  return children;
}

/**
 * Hilfshook: liefert den Access-Zustand für Komponenten, die den role_label
 * benötigen (z.B. um Mentor-CTA anzuzeigen).
 */
export function useHpaAccess() {
  const [state, setState] = useState({ loading: true, access: null });
  useEffect(() => {
    let alive = true;
    hpaGetAccess()
      .then((data) => alive && setState({ loading: false, access: data }))
      .catch(() => alive && setState({ loading: false, access: null }));
    return () => {
      alive = false;
    };
  }, []);
  return state;
}
